import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import useUpload from "hooks/useUpload";

const ALLOWED_FILE_EXTENSIONS = [
  '.jpg', '.jpeg', '.jif', '.jfif', '.png', '.gif', '.webp', '.heic', '.heif', '.pdf',
  '.tiff', '.tif', '.bmp', '.avif', '.dcm', '.dicom', '.ima',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.odt', '.ods', '.odp',
  '.txt', '.csv', '.rtf',
  '.mp4', '.mov', '.avi', '.mkv',
  '.mp3', '.wav', '.m4a',
  '.zip', '.rar', '.7z',
  '.eml', '.msg',
]

function getFileExtension(filename) {
  const match = /\.[^.]+$/.exec(filename || '')
  return match ? match[0].toLowerCase() : ''
}

const App = (props) => {
  const $file = useRef(null);
  const { t } = useTranslation();
  const upload = useUpload();
  const [uploading, setUploading] = useState();
  const [files, setFiles] = useState([]);

  const onChange = async (e) => {
    if (uploading) return;

    // multer aborts the whole S3 batch when one file in it fails validation, rolling back
    // files that already uploaded successfully — so a valid + invalid file mixed in the same
    // request lose both. Keep unsupported files out of the batch instead of sending them.
    const array = Array.from(e.target.files);
    const allowed = array.filter((file) => ALLOWED_FILE_EXTENSIONS.includes(getFileExtension(file.name)));
    const rejectedNames = array
      .filter((file) => !ALLOWED_FILE_EXTENSIONS.includes(getFileExtension(file.name)))
      .map((file) => file.name);

    if (rejectedNames.length > 0) {
      window.notify({
        message: t(
          'ehr:upload_error_unsupported_type_named',
          'Tipo de archivo no permitido: {{files}}',
          { context: 'files', files: rejectedNames.join(', ') }
        ),
        type: 'danger',
        title: 'Error',
      });
    }

    if (allowed.length === 0) return;

    setUploading(true);
    const links = await upload(allowed);
    if (links) {
      props.onChange({ files: [...files, ...links] });
    }
    setUploading(false);
  };

  const remove = (url) => {
    const array = files.filter((file) => file !== url);
    props.onChange({ files: array });
  };

  useEffect(() => {
    if (Array.isArray(props.value)) {
      setFiles(JSON.clone(props.value));
    }
  }, [props.value]);

  return (
    <div className="d-flex align-items-center text-nowrap">
      {props.editable && (
        <input
          ref={$file}
          type="file"
          multiple
          onChange={onChange}
          className={`${styles.input} form-control rounded-0 border-0 d-none`}
        />
      )}
      {uploading ? (
        <div className="flex-fill text-center">
          <div
            className="spinner-border text-secondary spinner-border-sm ms-2"
            role="status"
          >
            <span className="visually-hidden">{t("uploading")}</span>
          </div>
        </div>
      ) : files.length === 0 && props.editable ? (
        <button
          disabled={!props.editable}
          className="btn btn-sm bg-light text-success w-100 m-1 border"
          onClick={() => $file.current.click()}
          disabled={uploading}
        >
          {t("upload", "Subir")}
        </button>
      ) : (
        files.length > 0 && (
          <>
            <button
              className="btn btn-sm"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
            >
              {t("form_field_type_files_label", "Archivos")}{" "}
              <small className="text-secondary">({files.length})</small>
            </button>
            {/* Bug real (auditoría 2026-08-24, responsive): maxWidth fijo de
                350px excede un viewport de 320px, sin que Bootstrap lo
                ajuste solo. */}
            <div className="dropdown-menu p-0" style={{ maxWidth: "min(350px, calc(100vw - 32px))" }}>
              {props.editable && (
                <button
                  className="btn btn-sm bg-light text-success w-100 border-bottom rounded-0 p-2"
                  onClick={() => $file.current.click()}
                  disabled={uploading}
                >
                  {t("upload_new_files", "Seleccionar mas archivos")}
                </button>
              )}
              {files.map((file) => (
                <div
                  key={file}
                  className="d-flex align-items-center overflow-hidden"
                >
                  {props.editable && (
                    <button className="btn btn-sm" onClick={() => remove(file)}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-trash"
                        viewBox="0 0 16 16"
                      >
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                        <path
                          fillRule="evenodd"
                          d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                        />
                      </svg>
                    </button>
                  )}
                  <a href={file} target="_blank" className="p-2">
                    {file.replace("https://", "")}
                  </a>
                </div>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
};

export default App;
